import React, { useState, useEffect } from 'react';
import './EditTopicForm.css';

const TOPIC_TYPE_OPTIONS = [
  { label: 'foundation', value: 'foundation' },
  { label: 'curriculum', value: 'curriculum' },
  { label: 'advanced', value: 'advanced' }
];

/**
 * @param {Object} topic - The existing topic object to edit
 * @param {Function} onSubmit - Callback receiving updated data: onSubmit(formData)
 * @param {Function} onCancel - Callback triggered on cancel
 * @param {boolean} isSubmitting - External loading state
 */
export default function EditTopicForm({
  topic = {},
  onSubmit,
  onCancel,
  isSubmitting = false,
}) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    order: 0,
    icon: '',
    topicType: 'standard',
  });

  const [errors, setErrors] = useState({});

  // Pre-fill form when the topic prop loads or changes
  useEffect(() => {
    if (topic && Object.keys(topic).length > 0) {
      setFormData({
        name: topic.name || '',
        description: topic.description || '',
        order: topic.order ?? 0,
        icon: topic.icon || '',
        topicType: topic.topicType || 'standard',
      });
    }
  }, [topic]);

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Topic name is required.';
    }
    if (formData.order === '' || isNaN(formData.order) || Number(formData.order) < 0) {
      newErrors.order = 'Order must be a non-negative number.';
    }
    if (!formData.topicType) {
      newErrors.topicType = 'Please select a topic type.';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'number' ? (value === '' ? '' : Number(value)) : value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    if (onSubmit) {
      onSubmit(formData);
    }
  };

  return (
    <form className="edit-topic-form" onSubmit={handleSubmit} noValidate>

      {/* Name */}
      <div className="form-group">
        <label htmlFor="topic-name">Topic Name *</label>
        <input
          id="topic-name"
          name="name"
          type="text"
          value={formData.name}
          onChange={handleChange}
          placeholder="e.g., Fractions & Decimals"
          className={errors.name ? 'input-error' : ''}
          disabled={isSubmitting}
        />
        {errors.name && <span className="field-error">{errors.name}</span>}
      </div>

      {/* Description */}
      <div className="form-group">
        <label htmlFor="topic-description">Description</label>
        <textarea
          id="topic-description"
          name="description"
          rows={3}
          value={formData.description}
          onChange={handleChange}
          placeholder="Brief summary of what this topic covers..."
          disabled={isSubmitting}
        />
      </div>

      {/* Row: Order & Icon */}
      <div className="form-row">
        <div className="form-group flex-1">
          <label htmlFor="topic-order">Display Order *</label>
          <input
            id="topic-order"
            name="order"
            type="number"
            min="0"
            step="1"
            value={formData.order}
            onChange={handleChange}
            className={errors.order ? 'input-error' : ''}
            disabled={isSubmitting}
          />
          {errors.order && <span className="field-error">{errors.order}</span>}
        </div>

        <div className="form-group flex-1">
          <label htmlFor="topic-icon">Icon (URL, Emoji, or Class)</label>
          <input
            id="topic-icon"
            name="icon"
            type="text"
            value={formData.icon}
            onChange={handleChange}
            placeholder="e.g. 📐 or /icons/fraction.svg"
            disabled={isSubmitting}
          />
        </div>
      </div>

      {/* Topic Type (Dropdown) */}
      <div className="form-group">
        <label htmlFor="topic-type">Topic Type *</label>
        <select
          id="topic-type"
          name="topicType"
          value={formData.topicType}
          onChange={handleChange}
          className={errors.topicType ? 'input-error' : ''}
          disabled={isSubmitting}
        >
          {TOPIC_TYPE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        {errors.topicType && <span className="field-error">{errors.topicType}</span>}
      </div>

      {/* Action Buttons */}
      <div className="form-actions">
        {onCancel && (
          <button
            type="button"
            className="btn btn--secondary"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Cancel
          </button>
        )}
        <button
          type="submit"
          className="btn btn--primary"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </form>
  );
}